import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FormationCoverImage } from './FormationCoverImage';
import {
  formationAuthorInitials,
  formationCardGradient,
} from './utils';
import './lo-formation-cards.css';

export interface LoFormationMarketplaceCardProps {
  id: string;
  title: string;
  href: string;
  priceLabel: string;
  category?: string | null;
  levelLabel?: string | null;
  authorName?: string | null;
  coverImageUrl?: string | null;
  rating?: number | null;
  reviewCount?: number | null;
  featuredLabel?: string | null;
  isDemo?: boolean;
  demoBadgeLabel?: string;
  className?: string;
}

export function LoFormationMarketplaceCard({
  id,
  title,
  href,
  priceLabel,
  category,
  levelLabel,
  authorName,
  coverImageUrl,
  rating,
  reviewCount,
  featuredLabel,
  isDemo = false,
  demoBadgeLabel = 'Démo',
  className,
}: LoFormationMarketplaceCardProps) {
  return (
    <Link to={href} className={cn('lo-fc-market', className)}>
      <div className="lo-fc-market__visual">
        {featuredLabel ? (
          <span className="lo-fc-market__featured">{featuredLabel}</span>
        ) : null}
        {isDemo ? (
          <span className="lo-fc-market__demo">{demoBadgeLabel}</span>
        ) : null}
        <FormationCoverImage
          coverImageUrl={coverImageUrl}
          className="h-full w-full object-cover"
          fallback={(
            <div
              className="lo-fc-market__gradient"
              style={{ background: formationCardGradient(id) }}
            />
          )}
        />
        {levelLabel ? (
          <span className="lo-fc-market__badge">{levelLabel}</span>
        ) : null}
      </div>
      <div className="lo-fc-market__body">
        {category ? (
          <p className="lo-fc-market__category">{category}</p>
        ) : null}
        <h3 className="lo-fc-market__title">{title}</h3>
        {authorName ? (
          <div className="lo-fc-market__author">
            <span className="lo-fc-market__avatar" aria-hidden>
              {formationAuthorInitials(authorName)}
            </span>
            <span className="lo-fc-market__author-name">{authorName}</span>
          </div>
        ) : null}
        <div className="lo-fc-market__footer">
          {rating != null && rating > 0 ? (
            <span className="lo-fc-market__rating">
              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden />
              <strong>{rating.toFixed(1)}</strong>
              {reviewCount != null && reviewCount > 0 ? (
                <span>({reviewCount})</span>
              ) : null}
            </span>
          ) : (
            <span />
          )}
          <span className={cn('lo-fc-market__price', isDemo && 'lo-fc-market__price--demo')}>
            {priceLabel}
            {isDemo ? <span className="lo-fc-market__price-demo">{demoBadgeLabel}</span> : null}
          </span>
        </div>
      </div>
    </Link>
  );
}

interface GridProps {
  children: ReactNode;
  className?: string;
  variant?: 'market' | 'learner';
}

export function LoFormationCardGrid({ children, className, variant = 'market' }: GridProps) {
  return (
    <div className={cn(
      'lo-fc-grid',
      variant === 'learner' && 'lo-fc-grid--learner',
      className,
    )}
    >
      {children}
    </div>
  );
}

export function LoFormationCardList({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('lo-fc-list', className)}>{children}</div>;
}
